/** Provider de autenticação Azure Entra ID agnóstico */
import React, { useEffect, useState, useMemo, useCallback, ReactNode, useRef } from 'react';
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  AccountInfo,
  AuthenticationResult,
} from '@azure/msal-browser';
import { AuthContext } from '../context';
import { UserProfile, AuthContextState } from '../types';
import logger from '@/shared/lib/logger';
import { setCurrentToken } from '../utils/tokenManager';
import { registerTokenRefresh, clearTokenRefresh } from '../utils/tokenRefresh';

interface AzureAuthProviderProps {
  children: ReactNode;
}

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

const clientId = import.meta.env.VITE_AZURE_CLIENT_ID || '';
const tenantId = import.meta.env.VITE_AZURE_TENANT_ID || '';
const authorityEnv = (import.meta.env.VITE_AZURE_AUTHORITY || '').replace(/\/+$/, '');
const authority = authorityEnv || (tenantId ? `https://login.microsoftonline.com/${tenantId}` : '');
const redirectUri = import.meta.env.VITE_AZURE_REDIRECT_URI || `${window.location.origin}/callback`;
const postLogoutRedirectUri = import.meta.env.VITE_AZURE_POST_LOGOUT_REDIRECT_URI || window.location.origin;

const resolvedClientId = clientId || '00000000-0000-0000-0000-000000000000';
const resolvedAuthority = authority || 'https://login.microsoftonline.com/common';

const getScopes = (): string[] => {
  const scopesEnv = import.meta.env.VITE_AZURE_SCOPES;
  if (scopesEnv) {
    const scopes = scopesEnv.split(',').map((s: string) => s.trim());
    logger.info('[AzureAuthProvider] Usando scopes do .env', { scopes });
    return scopes;
  }
  
  logger.warn('[AzureAuthProvider] VITE_AZURE_SCOPES não encontrado, usando padrão');
  return ['openid', 'profile', 'email'];
};

const idTokenFromResult = (result: AuthenticationResult): string | null => {
  const id = result.idToken?.trim();
  return id || null;
};

const mapAzureUser = (account: AccountInfo): UserProfile => {
  return {
    id: account.idTokenClaims?.oid as string || account.localAccountId || '',
    email: account.username || account.idTokenClaims?.email as string || '',
    name: account.name || account.idTokenClaims?.name as string || 'Unknown',
    roles: (account.idTokenClaims?.roles as string[]) || [],
    avatarUrl: undefined,
    sub: account.idTokenClaims?.sub as string || account.idTokenClaims?.oid as string,
    preferred_username: account.username || account.idTokenClaims?.preferred_username as string,
    given_name: account.idTokenClaims?.given_name as string,
    family_name: account.idTokenClaims?.family_name as string,
  };
};
export const AzureAuthProvider: React.FC<AzureAuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  const tokenRef = useRef<string | null>(null);
  const msalInstance = useMemo(() => {
    const msalConfig = {
      auth: {
        clientId: resolvedClientId,
        authority: resolvedAuthority,
        redirectUri,
        postLogoutRedirectUri,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'sessionStorage' as const,
        storeAuthStateInCookie: false,
      },
    };
    return new PublicClientApplication(msalConfig);
  }, []);
  const getAccount = useCallback((): AccountInfo | null => {
    if (DISABLE_AUTH) {
      return null;
    }
    const accounts = msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }, [msalInstance]);
  const updateToken = useCallback(async (minValidity: number = 30): Promise<string | null> => {
    if (DISABLE_AUTH) {
      const dummyToken = 'dummy-token';
      tokenRef.current = dummyToken;
      setCurrentToken(dummyToken);
      return dummyToken;
    }

    const account = getAccount();
    if (!account) {
      logger.debug('[AzureAuthProvider] updateToken: No account found');
      return null;
    }

    logger.debug('[AzureAuthProvider] Updating token', {
      minValidity,
      accountUsername: account.username,
    });

    msalInstance.setActiveAccount(account);

    try {
      const result = await msalInstance.acquireTokenSilent({
        account,
        scopes: getScopes(),
      });

      const newToken = idTokenFromResult(result);
      if (!newToken) {
        logger.error('[AzureAuthProvider] acquireTokenSilent não retornou id token');
        return null;
      }

      logger.info('[AzureAuthProvider] ID token adquirido silenciosamente', {
        tokenLength: newToken.length,
        expiresOn: result.expiresOn?.toISOString(),
      });

      tokenRef.current = newToken;
      setCurrentToken(newToken);
      setToken(newToken);

      return newToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        logger.warn('[AzureAuthProvider] Refresh requer interação do usuário', {
          errorCode: error.errorCode,
        });
        return null;
      }
      logger.error('[AzureAuthProvider] Falha ao renovar token', {
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  }, [msalInstance, getAccount]);
  const updateAuthState = useCallback(async (skipTokenUpdate = false) => {
    logger.debug('[AzureAuthProvider] Updating auth state', { skipTokenUpdate });

    try {
      let currentTokenValue = tokenRef.current;
      
      /** Tenta renovar token se não for para pular */
      if (!skipTokenUpdate) {
        try {
          const newToken = await updateToken(60);
          if (newToken) {
            currentTokenValue = newToken;
          }
        } catch (error) {
          logger.warn('[AzureAuthProvider] Token não pôde ser renovado', { error });
        }
      }

      const account = getAccount();
      const isAuth = !!account;
      
      if (isAuth && account) {
        const userProfile = mapAzureUser(account);

        logger.info('[AzureAuthProvider] Usuário autenticado', {
          email: userProfile.email,
          name: userProfile.name,
          hasToken: !!currentTokenValue,
          tokenLength: currentTokenValue?.length || 0,
        });

        setIsAuthenticated(true);
        setUser(userProfile);
        
        if (skipTokenUpdate && currentTokenValue) {
          setCurrentToken(currentTokenValue);
        }
      } else {
        logger.info('[AzureAuthProvider] Usuário não autenticado');
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
        tokenRef.current = null;
        setCurrentToken(null);
      }
    } catch (error) {
      logger.error('[AzureAuthProvider] Falha ao atualizar estado de auth', { error });
    }
  }, [getAccount, updateToken]);
  const login = useCallback(() => {
    if (DISABLE_AUTH) {
      logger.debug('[AzureAuthProvider] Auth desabilitada, simulando login');
      window.location.href = window.location.origin;
      return;
    }

    logger.info('[AzureAuthProvider] Iniciando redirect de login', {
      redirectUri: window.location.origin + '/callback',
      scopes: getScopes(),
    });

    msalInstance.loginRedirect({
      scopes: getScopes(),
      redirectStartPage: window.location.origin + '/callback',
    }).catch((error) => {
      logger.error('[AzureAuthProvider] Falha no redirect de login', {
        error: error.message,
        errorCode: error.errorCode,
      });
    });
  }, [msalInstance]);
  const logout = useCallback(() => {
    if (DISABLE_AUTH) {
      logger.debug('[AzureAuthProvider] Auth desabilitada, simulando logout');
      clearTokenRefresh();
      window.location.href = window.location.origin;
      return;
    }

    const account = getAccount();
    if (account) {
      msalInstance.logoutRedirect({
        account,
        postLogoutRedirectUri,
      }).catch((error) => {
        logger.error('[AzureAuthProvider] Falha no redirect de logout:', error);
      });
    } else {
      window.location.href = window.location.origin;
    }

    tokenRef.current = null;
    setCurrentToken(null);
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
    clearTokenRefresh();
  }, [msalInstance, getAccount]);
  const hasRole = useCallback((role: string): boolean => {
    return user?.roles.includes(role) || false;
  }, [user]);
  useEffect(() => {
    const initAuth = async () => {
      logger.info('[AzureAuthProvider] Initializing MSAL', {
        clientId: resolvedClientId ? `${resolvedClientId.substring(0, 8)}...` : 'NOT SET',
        authority: resolvedAuthority,
        redirectUri,
      });

      if (DISABLE_AUTH) {
        logger.warn('[AzureAuthProvider] Auth disabled via VITE_DISABLE_AUTH');
        const dummyToken = 'dummy-token';
        
        tokenRef.current = dummyToken;
        setCurrentToken(dummyToken);
        setToken(dummyToken);
        
        setIsAuthenticated(true);
        setUser({
          id: 'dummy_id',
          email: 'dummy@example.com',
          name: 'Usuário Dummy',
          roles: [],
        });
        setIsLoading(false);
        return;
      }

      try {
        await msalInstance.initialize();
        logger.info('[AzureAuthProvider] MSAL inicializado, processando redirect...');

        /** Processa o redirect do Azure após login */
        const result = await msalInstance.handleRedirectPromise();
        
        if (result?.account) {
          logger.info('[AzureAuthProvider] Conta encontrada no redirect', {
            username: result.account.username,
            name: result.account.name,
          });
          msalInstance.setActiveAccount(result.account);
        }

        if (result) {
          const redirectId = idTokenFromResult(result);
          if (redirectId) {
            logger.info('[AzureAuthProvider] ID token recebido do redirect', {
              tokenLength: redirectId.length,
            });
            tokenRef.current = redirectId;
            setCurrentToken(redirectId);
            setToken(redirectId);
          }
        }

        const account = getAccount();
        if (account) {
          logger.info('[AzureAuthProvider] Conta ativa definida', {
            username: account.username,
            name: account.name,
          });
          msalInstance.setActiveAccount(account);
          
          if (!tokenRef.current) {
            logger.info('[AzureAuthProvider] Sem id token do redirect, adquirindo silenciosamente...');
            const newToken = await updateToken(60);
            if (newToken) {
              logger.info('[AzureAuthProvider] Token adquirido na inicialização', {
                tokenLength: newToken.length,
              });
            }
          }
        }

        await updateAuthState(true);
        
        /** Registra função de refresh para uso pelo httpClient */
        registerTokenRefresh(updateToken);
        logger.info('[AzureAuthProvider] Função de refresh registrada');
      } catch (error) {
        logger.error('[AzureAuthProvider] Falha na inicialização', {
          error: error instanceof Error ? error.message : String(error),
        });
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    /** Verifica estado de auth a cada 5 minutos */
    const authCheckInterval = setInterval(() => {
      if (!isLoading) {
        updateAuthState();
      }
    }, 300000);

    return () => {
      clearInterval(authCheckInterval);
    };
  }, [msalInstance, isLoading, getAccount, updateAuthState]);
  const value = useMemo<AuthContextState>(() => ({
    isAuthenticated,
    isLoading,
    user,
    token,
    login,
    logout,
    hasRole,
  }), [isAuthenticated, isLoading, user, token, login, logout, hasRole]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
